import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useSearchParams } from "react-router-dom";
import { PawPrint, Send, CheckCircle2, Phone, Mail, MapPin } from "lucide-react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { API_BASE } from "@/lib/api";

const CONTACT_METHODS = [
  { value: "phone", label: "Phone call" },
  { value: "text", label: "Text message" },
  { value: "email", label: "Email" },
];

const TIMEFRAMES = [
  { value: "asap", label: "As soon as possible" },
  { value: "1-2 weeks", label: "Within 1–2 weeks" },
  { value: "next month", label: "Within the next month" },
  { value: "flexible", label: "I'm flexible" },
];

const SOURCES = [
  "Google search",
  "Instagram",
  "Facebook",
  "TikTok",
  "Friend or family",
  "Veterinarian referral",
  "Other dog owner",
  "Drove past / saw sign",
  "Other",
];

const emptyLead = {
  ownerName: "",
  phone: "",
  email: "",
  address: "",
  dogName: "",
  breed: "",
  age: "",
  mainBehaviorIssue: "",
  goals: "",
  preferredContact: "phone",
  preferredTimeframe: "1-2 weeks",
  source: "",
  referrer: "",
  website: "", // honeypot
};

export default function PublicIntakePage() {
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({
    ...emptyLead,
    referrer: searchParams.get("source") || "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    document.title = "Request a Training Consultation";
  }, []);

  function update(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    // Honeypot — silently bail on bot
    if (form.website) {
      setDone(true);
      return;
    }
    if (!form.ownerName.trim() || !form.dogName.trim()) {
      setError("Please tell us your name and your dog's name.");
      return;
    }
    if (!form.phone.trim() && !form.email.trim()) {
      setError("Please give us either a phone number or email so we can reach you.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_BASE}/public/leads`, form);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-amber-50 via-slate-50 to-white px-4 pb-24 pt-10 md:pb-20 md:pt-16">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="mx-auto max-w-xl"
          data-testid="lead-success"
        >
          <Card className="rounded-3xl shadow-md">
            <CardContent className="p-8 text-center md:p-10">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100 text-green-700">
                <CheckCircle2 size={32} />
              </div>
              <h1 className="text-2xl font-bold tracking-tight md:text-3xl">Request received</h1>
              <p className="mt-3 text-slate-600">
                Thanks for reaching out! We'll review your details and get back to you
                {form.preferredContact === "email"
                  ? " by email"
                  : form.preferredContact === "text"
                    ? " by text"
                    : " by phone"}{" "}
                shortly.
              </p>
              <div className="mt-6 rounded-2xl bg-slate-50 p-4 text-left text-sm text-slate-600">
                <p>
                  <span className="font-semibold text-slate-900">What's next?</span> A trainer will
                  reach out within 1–2 business days to schedule a free intro call and review your
                  dog's goals.
                </p>
              </div>
              <Button
                data-testid="submit-another-btn"
                variant="outline"
                onClick={() => {
                  setForm({ ...emptyLead, referrer: form.referrer });
                  setDone(false);
                }}
                className="mt-6 rounded-2xl"
              >
                Submit another request
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 via-slate-50 to-white px-4 pb-24 pt-8 md:pb-20 md:pt-14">
      <div className="mx-auto max-w-2xl">
        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 text-center md:mb-10"
        >
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-900 text-white shadow-md">
            <PawPrint size={26} />
          </div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900 md:text-4xl">
            Request a Training Consultation
          </h1>
          <p className="mx-auto mt-3 max-w-md text-slate-600">
            Tell us a bit about your dog and we'll match you with a trainer who can help.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className="rounded-3xl shadow-md">
            <CardContent className="p-5 md:p-8">
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="lead-form">
                {/* Honeypot — hidden from real users, attractive to bots */}
                <div aria-hidden="true" style={{ position: "absolute", left: "-9999px" }}>
                  <label>
                    Website
                    <input
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      value={form.website}
                      onChange={(e) => update("website", e.target.value)}
                    />
                  </label>
                </div>

                {/* Section: You */}
                <section className="space-y-4">
                  <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                    About you
                  </h2>
                  <div className="grid gap-4 md:grid-cols-2">
                    <Field label="Your name *">
                      <input
                        data-testid="lead-owner-name"
                        required
                        value={form.ownerName}
                        onChange={(e) => update("ownerName", e.target.value)}
                        className={inputCls}
                        placeholder="Jane Doe"
                      />
                    </Field>
                    <Field label="City / area">
                      <div className="relative">
                        <MapPin size={16} className="absolute left-3 top-3 text-slate-400" />
                        <input
                          data-testid="lead-address"
                          value={form.address}
                          onChange={(e) => update("address", e.target.value)}
                          className={`${inputCls} pl-9`}
                          placeholder="Atlanta, GA"
                        />
                      </div>
                    </Field>
                    <Field label="Phone">
                      <div className="relative">
                        <Phone size={16} className="absolute left-3 top-3 text-slate-400" />
                        <input
                          data-testid="lead-phone"
                          type="tel"
                          value={form.phone}
                          onChange={(e) => update("phone", e.target.value)}
                          className={`${inputCls} pl-9`}
                          placeholder="(555) 123-4567"
                        />
                      </div>
                    </Field>
                    <Field label="Email">
                      <div className="relative">
                        <Mail size={16} className="absolute left-3 top-3 text-slate-400" />
                        <input
                          data-testid="lead-email"
                          type="email"
                          value={form.email}
                          onChange={(e) => update("email", e.target.value)}
                          className={`${inputCls} pl-9`}
                          placeholder="you@example.com"
                        />
                      </div>
                    </Field>
                  </div>
                  <p className="text-xs text-slate-500">We need either a phone or email to reach you.</p>
                </section>

                {/* Section: Dog */}
                <section className="space-y-4">
                  <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                    About your dog
                  </h2>
                  <div className="grid gap-4 md:grid-cols-2">
                    <Field label="Dog's name *">
                      <input
                        data-testid="lead-dog-name"
                        required
                        value={form.dogName}
                        onChange={(e) => update("dogName", e.target.value)}
                        className={inputCls}
                        placeholder="Bentley"
                      />
                    </Field>
                    <Field label="Breed">
                      <input
                        data-testid="lead-breed"
                        value={form.breed}
                        onChange={(e) => update("breed", e.target.value)}
                        className={inputCls}
                        placeholder="Golden Retriever"
                      />
                    </Field>
                    <Field label="Age">
                      <input
                        data-testid="lead-age"
                        value={form.age}
                        onChange={(e) => update("age", e.target.value)}
                        className={inputCls}
                        placeholder="2 years"
                      />
                    </Field>
                  </div>
                  <Field label="Main behavior issue">
                    <textarea
                      data-testid="lead-behavior-issue"
                      value={form.mainBehaviorIssue}
                      onChange={(e) => update("mainBehaviorIssue", e.target.value)}
                      className={`${inputCls} min-h-24`}
                      placeholder="e.g. Pulls hard on the leash, reactive around other dogs..."
                    />
                  </Field>
                  <Field label="What's your training goal?">
                    <textarea
                      data-testid="lead-goals"
                      value={form.goals}
                      onChange={(e) => update("goals", e.target.value)}
                      className={`${inputCls} min-h-20`}
                      placeholder="e.g. Loose-leash walking, off-leash recall, calm around guests..."
                    />
                  </Field>
                </section>

                {/* Section: Logistics */}
                <section className="space-y-4">
                  <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                    Logistics
                  </h2>
                  <div className="grid gap-4 md:grid-cols-2">
                    <Field label="Preferred contact method">
                      <select
                        data-testid="lead-preferred-contact"
                        value={form.preferredContact}
                        onChange={(e) => update("preferredContact", e.target.value)}
                        className={inputCls}
                      >
                        {CONTACT_METHODS.map((m) => (
                          <option key={m.value} value={m.value}>
                            {m.label}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <Field label="When would you like to start?">
                      <select
                        data-testid="lead-preferred-timeframe"
                        value={form.preferredTimeframe}
                        onChange={(e) => update("preferredTimeframe", e.target.value)}
                        className={inputCls}
                      >
                        {TIMEFRAMES.map((t) => (
                          <option key={t.value} value={t.value}>
                            {t.label}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <Field label="How did you hear about us?">
                      <select
                        data-testid="lead-source"
                        value={form.source}
                        onChange={(e) => update("source", e.target.value)}
                        className={`${inputCls} md:col-span-2`}
                      >
                        <option value="">Select one...</option>
                        {SOURCES.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </Field>
                  </div>
                </section>

                {error && (
                  <p
                    data-testid="lead-error"
                    className="rounded-xl bg-red-50 p-3 text-sm text-red-800"
                  >
                    {error}
                  </p>
                )}

                <Button
                  data-testid="lead-submit"
                  type="submit"
                  disabled={submitting}
                  className="w-full rounded-2xl py-6 text-base shadow-sm"
                >
                  <Send size={18} className="mr-2" />
                  {submitting ? "Sending..." : "Request a Training Consultation"}
                </Button>

                <p className="text-center text-xs text-slate-500">
                  By submitting, you agree to be contacted by our team about training services.
                </p>
              </form>
            </CardContent>
          </Card>
        </motion.div>

        <p className="mt-6 text-center text-xs text-slate-400">
          Are you a trainer? <a href="/" className="underline">Sign in here</a>
        </p>
      </div>
    </div>
  );
}

const inputCls =
  "w-full rounded-xl border border-slate-200 bg-white p-3 font-normal outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-200";

function Field({ label, children }) {
  return (
    <label className="block space-y-1 text-sm font-medium">
      {label}
      {children}
    </label>
  );
}
